import { useEffect, useState, useRef, useContext } from "react";
import AuthContext from "../context/AuthContext";
import axiosInstance from "../api/axios";

export function useBadgeCounters({ ws, status }) {
  const { user, token } = useContext(AuthContext);
  const [counters, setCounters] = useState({ messages: 0, notifications: 0 });
  const [threadCounters, setThreadCounters] = useState({}); // { [threadId]: unread count }
  const subRef = useRef(false);

  const SUPPRESS_MS = 250;
  const countersTimerRef = useRef(null);
  const threadTimersRef = useRef(new Map()); // threadId -> timeout id

  // init load
  useEffect(() => {
    let canceled = false;
    const fetchBadges = async () => {
      try {
        const res = await axiosInstance.get(`/badges/me/counters/`, {
          headers: { Authorization: `Token ${token}` },
        });

        if (canceled) return;
        setCounters(res.data);
      } catch (err) {
        console.error("Error getting badge details", err);
      }
    };

    fetchBadges();

    return () => {
      canceled = true;
    };
  }, [token]);

  //   subscribe

  useEffect(() => {
    if (!ws || status !== "connected" || !user) return;
    ws.send(JSON.stringify({ type: "badge.subscribe" }));
    subRef.current = true;

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "unsubscribe", topic: `badges-${user.id}` }));
      }
      subRef.current = false;
    };
  }, [ws, status, user?.id]);

  // listen for updates

  useEffect(() => {
    if (!ws || status !== "connected" || !user) return;

    // add slight debounce to badges - without delay badges flash on active thread
    // beacuse last_read_at doesn't update fast enough

    const scheduleCounters = (payload) => {
      if (countersTimerRef.current) clearTimeout(countersTimerRef.current);
      countersTimerRef.current = setTimeout(() => {
        setCounters((prev) => ({ ...prev, ...payload }));
      }, SUPPRESS_MS);
    };

    const scheduleThread = (threadId, unread) => {
      const key = String(threadId);
      const existing = threadTimersRef.current.get(key);
      if (existing) clearTimeout(existing);
      const t = setTimeout(() => {
        setThreadCounters((prev) => ({ ...prev, [key]: unread }));
        threadTimersRef.current.delete(key);
      }, SUPPRESS_MS);
      threadTimersRef.current.set(key, t);
    };

    const onMsg = (e) => {
      let msg;
      try {
        msg = JSON.parse(e.data);
      } catch {
        return;
      }

      if (msg.type === "badge.counters") {
        // Debounce navbar/global badge updates
        scheduleCounters(msg);
      } else if (msg.type === "badge.thread") {
        // Debounce per-thread badge updates (prevents flash in sidebar/active thread)
        scheduleThread(msg.thread_id, msg.unread);
      } else if (msg.type === "badge.thread.bulk") {
        if (countersTimerRef.current) clearTimeout(countersTimerRef.current);
        countersTimerRef.current = setTimeout(() => {
          const map = Object.fromEntries(
            (msg.items || []).map((it) => [String(it.thread_id), it.unread])
          );
          setThreadCounters(map);
        }, SUPPRESS_MS);
      }
    };
    ws.addEventListener("message", onMsg);
    return () => {
      ws.removeEventListener("message", onMsg);
      if (countersTimerRef.current) {
        clearTimeout(countersTimerRef.current);
        countersTimerRef.current = null;
      }
      // clear all per-thread timeouts
      for (const t of threadTimersRef.current.values()) clearTimeout(t);
      threadTimersRef.current.clear();
    };
  }, [ws, status, user?.id]);

  return { counters, threadCounters, isSubscribed: subRef.current };
}
